import { Localizable } from '@components/localizable/Localizable'
import {
  WalletCardAsset,
  WalletField,
} from '@components/membership/program/wallet/types'
import WalletAssetUploader from '@components/membership/program/wallet/WalletAssetUploader'
import WalletCardPreview from '@components/membership/program/wallet/WalletCardPreview'
import WalletFormField from '@components/membership/program/wallet/WalletFormField'
import Page from '@components/Page/Page'
import { useLocalizable } from '@contexts/LocalizableContext'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import {
  selectSelectedProgram,
  updateProgram,
} from '@store/membership/ProgramReducer'
import { emailRule, urlRule } from '@utils/rules'
import {
  App,
  Button,
  Card,
  Checkbox,
  Col,
  ColorPicker,
  Divider,
  Flex,
  Form,
  Input,
  Row,
  Space,
  Switch,
  Typography,
} from 'antd'
import { ReactNode, useEffect, useState } from 'react'

const { Title } = Typography

/** Generic, tenant-neutral sample member shown in the preview (no client name). */
const SAMPLE_MEMBER = {
  name: 'Alex Morgan',
  cardNumber: '0000 0000 0000',
}

/**
 * Hint for an unset color field: a dot of the actual default color plus a note.
 * The color pickers show "Transparent" when empty even though the pass renders
 * a real default, so this spells out what that default is.
 */
const DefaultColorHint = ({
  color,
  children,
}: {
  color: string
  children: ReactNode
}) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
    <span
      style={{
        width: 10,
        height: 10,
        borderRadius: '50%',
        background: color,
        border: '1px solid rgba(0, 0, 0, 0.15)',
        flexShrink: 0,
      }}
    />
    {children}
  </span>
)

const WalletCardTab = () => {
  const program = useAppSelector(selectSelectedProgram())
  const dispatch = useAppDispatch()
  const localizable = useLocalizable()
  const { message } = App.useApp()
  const [form] = Form.useForm()
  const [isLoading, setIsLoading] = useState<boolean>(false)

  const [logoAsset, setLogoAsset] = useState<WalletCardAsset>()
  const [iconAsset, setIconAsset] = useState<WalletCardAsset>()
  const [stripAsset, setStripAsset] = useState<WalletCardAsset>()
  const [heroAsset, setHeroAsset] = useState<WalletCardAsset>()

  // Which field the pointer/focus is on, shared by the form and the preview so
  // either side lights up the other. Null when nothing is hovered/focused.
  const [activeField, setActiveField] = useState<WalletField | null>(null)

  // Initialise the form only when the selected program changes — NOT on every
  // refetch of the same program. Uploading a wallet asset refetches the program
  // (to surface the new asset URL); keying this on `program` instead of its id
  // re-ran setFieldsValue on that refetch and wiped the admin's unsaved colour/
  // text edits. Keyed on the id, an asset upload leaves the form untouched.
  useEffect(() => {
    if (!program) return
    form.setFieldsValue({ ...program })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [program?.id])

  const langCode = localizable?.langCode ?? 'fr'
  const backgroundColor = Form.useWatch('walletBackgroundColor', form)
  const foregroundColor = Form.useWatch('walletForegroundColor', form)
  const labelColor = Form.useWatch('walletLabelColor', form)
  const showExpiry = Form.useWatch('walletShowExpiry', form)
  const supportEmail = Form.useWatch('walletSupportEmail', form)
  const supportPhone = Form.useWatch('walletSupportPhone', form)
  const supportUrl = Form.useWatch('walletSupportUrl', form)
  const showBrandLineIos = Form.useWatch('walletShowBrandLineIos', form)
  const brandLine = Form.useWatch([langCode, 'walletBrandLine'], form)
  const cardTitle = Form.useWatch([langCode, 'walletCardTitle'], form)
  const terms = Form.useWatch([langCode, 'walletTerms'], form)

  if (!program) {
    return <></>
  }

  const onFinish = (values: any) => {
    setIsLoading(true)
    dispatch(updateProgram(values))
      .then(() => message.success('Modifications saved'))
      .catch(() => message.error('Something went wrong'))
      .finally(() => setIsLoading(false))
  }

  const highlight = {
    activeField,
    onActiveFieldChange: setActiveField,
  }

  return (
    <Page>
      <Row gutter={[32, 32]}>
        <Col span={12}>
          <Flex vertical gap={32}>
            <Form form={form} layout="vertical" onFinish={onFinish}>
              <Form.Item
                name="id"
                hidden
                style={{ display: 'none' }}
                rules={[]}
              >
                <Input type="hidden" />
              </Form.Item>
              <Form.Item
                name="slug"
                hidden
                style={{ display: 'none' }}
                rules={[]}
              >
                <Input type="hidden" />
              </Form.Item>

              <Flex vertical className="w-full" gap={8}>
                <Space className="w-full space-between">
                  <Title level={5} className="m-0">
                    Wallet card
                  </Title>
                  <Button
                    style={{ border: 0 }}
                    type="primary"
                    loading={isLoading}
                    htmlType="submit"
                  >
                    Save
                  </Button>
                </Space>

                <Card>
                  <WalletFormField
                    field="brandLine"
                    {...highlight}
                    tags={[
                      { platform: 'apple', requirement: 'optional' },
                      { platform: 'google', requirement: 'required' },
                    ]}
                    hint="Next to the logo. Required on Google; on the Apple pass it's hidden unless the box above is ticked. Falls back to “Membership”."
                  >
                    <Localizable.Input
                      label="Brand line"
                      name="walletBrandLine"
                      max={40}
                    />
                    <Form.Item
                      name="walletShowBrandLineIos"
                      valuePropName="checked"
                      style={{ marginBottom: 0, marginTop: 4 }}
                    >
                      <Checkbox>Also show it on the Apple pass</Checkbox>
                    </Form.Item>
                  </WalletFormField>
                  <WalletFormField
                    field="cardTitle"
                    {...highlight}
                    tags={[{ platform: 'google', requirement: 'required' }]}
                    hint="The large title line on the Google pass. Falls back to “Membership”."
                  >
                    <Localizable.Input
                      label="Card title"
                      name="walletCardTitle"
                      max={50}
                    />
                  </WalletFormField>
                  <WalletFormField
                    field="terms"
                    {...highlight}
                    tags={[
                      { platform: 'apple', requirement: 'optional' },
                      { platform: 'google', requirement: 'optional' },
                    ]}
                    hint="Back of the card. Hidden when empty."
                  >
                    <Localizable.TextArea
                      label="Back of card terms"
                      name="walletTerms"
                      max={500}
                    />
                  </WalletFormField>

                  <Divider />

                  <Space size={24} wrap>
                    <WalletFormField
                      field="backgroundColor"
                      {...highlight}
                      tags={[
                        { platform: 'apple' },
                        { platform: 'google' },
                        { platform: 'app' },
                      ]}
                      hint={
                        <DefaultColorHint
                          color={program.primaryColor || '#111111'}
                        >
                          {program.primaryColor
                            ? "Card background. Defaults to the program's primary color."
                            : 'Card background. Defaults to a neutral dark.'}
                        </DefaultColorHint>
                      }
                    >
                      <Form.Item
                        label="Background color"
                        name="walletBackgroundColor"
                        getValueFromEvent={(color) => color.toHexString()}
                        style={{ marginBottom: 0 }}
                      >
                        <ColorPicker showText />
                      </Form.Item>
                    </WalletFormField>
                    <WalletFormField
                      field="foregroundColor"
                      {...highlight}
                      tags={[{ platform: 'apple' }, { platform: 'app' }]}
                      hint={
                        <DefaultColorHint color="#FFFFFF">
                          All text. Defaults to white. Google picks its own text
                          color, so this doesn't apply on Android.
                        </DefaultColorHint>
                      }
                    >
                      <Form.Item
                        label="Foreground color"
                        name="walletForegroundColor"
                        getValueFromEvent={(color) => color.toHexString()}
                        style={{ marginBottom: 0 }}
                      >
                        <ColorPicker showText />
                      </Form.Item>
                    </WalletFormField>
                    <WalletFormField
                      field="labelColor"
                      {...highlight}
                      tags={[{ platform: 'apple' }, { platform: 'app' }]}
                      hint={
                        <DefaultColorHint color="#FFFFFF">
                          Small field labels. Defaults to white. Not applied on
                          Android (Google auto-picks text color).
                        </DefaultColorHint>
                      }
                    >
                      <Form.Item
                        label="Label color"
                        name="walletLabelColor"
                        getValueFromEvent={(color) => color.toHexString()}
                        style={{ marginBottom: 0 }}
                      >
                        <ColorPicker showText />
                      </Form.Item>
                    </WalletFormField>
                  </Space>

                  <Divider />

                  <WalletFormField
                    field="support"
                    {...highlight}
                    tags={[
                      { platform: 'apple', requirement: 'optional' },
                      { platform: 'google', requirement: 'optional' },
                    ]}
                    hint="Back of the card / Google links."
                  >
                    <Form.Item
                      label="Support email"
                      name="walletSupportEmail"
                      rules={[emailRule()]}
                      style={{ marginBottom: 12 }}
                    >
                      <Input />
                    </Form.Item>
                    <Form.Item
                      label="Support phone"
                      name="walletSupportPhone"
                      style={{ marginBottom: 12 }}
                    >
                      <Input />
                    </Form.Item>
                    <Form.Item
                      label="Support URL"
                      name="walletSupportUrl"
                      rules={[urlRule()]}
                      style={{ marginBottom: 0 }}
                    >
                      <Input />
                    </Form.Item>
                  </WalletFormField>

                  <Divider />

                  <WalletFormField
                    field="showExpiry"
                    {...highlight}
                    tags={[{ platform: 'apple' }, { platform: 'google' }]}
                    hint="Adds an expiry row from the member's renewal date."
                  >
                    <Form.Item
                      label="Show expiry row"
                      name="walletShowExpiry"
                      valuePropName="checked"
                      style={{ marginBottom: 0 }}
                    >
                      <Switch />
                    </Form.Item>
                  </WalletFormField>
                </Card>
              </Flex>
            </Form>

            <WalletAssetUploader
              title="Logo"
              assetType="logo"
              field="logo"
              {...highlight}
              tags={[
                { platform: 'apple' },
                { platform: 'google' },
                { platform: 'app' },
              ]}
              image={{ width: 480, height: 150 }}
              sizeHint="PNG"
              hint="Google shows it on a white circle — use a mark that reads on white (a white-on-transparent logo disappears)."
              program={program}
              current={program.walletAssets?.logo}
              onAssetChange={setLogoAsset}
            />
            <WalletAssetUploader
              title="Icon"
              assetType="icon"
              field="icon"
              {...highlight}
              tags={[{ platform: 'apple' }]}
              image={{ width: 87, height: 87 }}
              sizeHint="PNG"
              hint="Notifications & pass list — not the card face"
              program={program}
              current={program.walletAssets?.icon}
              onAssetChange={setIconAsset}
            />
            <WalletAssetUploader
              title="Strip image"
              assetType="strip"
              field="strip"
              {...highlight}
              tags={[{ platform: 'apple' }]}
              image={{ width: 1125, height: 369 }}
              sizeHint="PNG"
              hint="Image band across the pass"
              program={program}
              current={program.walletAssets?.strip}
              onAssetChange={setStripAsset}
            />
            <WalletAssetUploader
              title="Hero image"
              assetType="hero"
              field="hero"
              {...highlight}
              tags={[{ platform: 'google' }]}
              image={{ width: 1032, height: 336 }}
              sizeHint="PNG"
              hint="Wide banner on the Google pass"
              program={program}
              current={program.walletAssets?.hero}
              onAssetChange={setHeroAsset}
            />
          </Flex>
        </Col>
        <Col span={12}>
          {/* Sticky, capped to the viewport and a flex column so the preview's
              switcher can stay fixed at the top while the card scrolls inside.
              `top` clears the fixed 64px MainHeader — a smaller offset tucks the
              switcher behind it. */}
          <div
            style={{
              position: 'sticky',
              top: 80,
              maxHeight: 'calc(100vh - 96px)',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <WalletCardPreview
              style={{ flex: 1, minHeight: 0 }}
              backgroundColor={backgroundColor}
              foregroundColor={foregroundColor}
              labelColor={labelColor}
              primaryColor={program.primaryColor}
              brandLine={brandLine}
              showBrandLineIos={!!showBrandLineIos}
              cardTitle={cardTitle}
              terms={terms}
              showExpiry={!!showExpiry}
              supportEmail={supportEmail}
              supportPhone={supportPhone}
              supportUrl={supportUrl}
              logo={logoAsset}
              icon={iconAsset}
              strip={stripAsset}
              hero={heroAsset}
              member={SAMPLE_MEMBER}
              programName={program[langCode]?.name}
              locale={langCode}
              {...highlight}
            />
          </div>
        </Col>
      </Row>
    </Page>
  )
}

export default WalletCardTab
